import React, { useState, useEffect, useMemo } from "react";
import { useRouter, useLocalSearchParams, useNavigation } from "expo-router";
import ThemedScreen from "@/components/ThemedScreen";
import { ThemedProgressBar } from "@/components/ThemedProgressBar";
import { CustomAlert } from "@/components/ThemedAlert";
import { useFirebase } from "@components/firebase/useFirebase";
import { createMemory } from "@/services/firebase/memory-functions";
import { getMedia } from "@/services/firebase/media-functions";
import { MemoryEditor } from "@/components/memory/MemoryEditor";
import { View, StyleSheet } from "react-native";
import { MemoryModel, MediaModel } from "common247";
import { navigateBackOrToList } from "@/utils/navigation";

/**
 * Screen for capturing a new memory with linear busy indicator during creation and media loading.
 */
export default function NewMemoryScreen() {
  const services = useFirebase();
  const router = useRouter();
  const navigation = useNavigation();
  const params = useLocalSearchParams<{ mediaIds?: string; title?: string }>();

  const [saving, setSaving] = useState(false);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [loadedMedia, setLoadedMedia] = useState<MediaModel[]>([]);

  useEffect(() => {
    if (!services || !params.mediaIds) return;
    const ids = params.mediaIds.split(",").map((s) => s.trim()).filter(Boolean);
    if (ids.length === 0) return;

    let isMounted = true;
    setLoadingMedia(true);

    Promise.all(ids.map((mediaId) => getMedia(services, { mediaId })))
      .then((responses) => {
        if (!isMounted) return;
        const validMedia: MediaModel[] = [];
        responses.forEach((r) => {
          if (r.status && r.media) {
            if (Array.isArray(r.media)) {
              validMedia.push(...r.media);
            } else {
              validMedia.push(r.media);
            }
          }
        });
        setLoadedMedia(validMedia);
      })
      .catch((err) => {
        console.error("Error loading pre-selected media:", err);
      })
      .finally(() => {
        if (isMounted) {
          setLoadingMedia(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [services, params.mediaIds]);

  const initialTitle = typeof params.title === "string" ? params.title : "";

  const templateMemory: MemoryModel = useMemo(
    () => ({
      date: new Date().toISOString().split("T")[0],
      ownerId: "",
      title: initialTitle,
      location: undefined,
      description: "",
      createdAt: 0,
      userRole: "owner",
      mediaIds: loadedMedia.map((m) => m.id!).filter(Boolean),
      media: loadedMedia,
    }),
    [initialTitle, loadedMedia],
  );

  const handleSave = async (formData: any) => {
    if (!services) return;
    setSaving(true);
    try {
      const res = await createMemory(services, formData);
      if (res.status) {
        navigateBackOrToList(router, "/(tabs)/my-memories", navigation);
      } else {
        CustomAlert.alert("Error", res.error || "Failed to create memory");
      }
    } catch (e) {
      CustomAlert.alert("Error", "Unexpected error while saving");
    } finally {
      setSaving(false);
    }
  };

  const isBusy = saving || loadingMedia;

  return (
    <ThemedScreen title="Capture Memory" loading={isBusy}>
      <View style={styles.container}>
        <ThemedProgressBar loading={loadingMedia} />
        <MemoryEditor
          key={`${initialTitle}-${loadedMedia.map((m) => m.id).join(",")}`}
          memory={templateMemory}
          isSaving={saving}
          onSave={handleSave}
          onCancel={() => navigateBackOrToList(router, "/(tabs)/my-memories", navigation)}
        />
      </View>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, padding: 16 },
});
